import React, {
  useState,
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
} from "react";
import { useIntl } from "react-intl";
import { Input } from "sr/helpers";
import DropDown from "sr/helpers/ui-components/DropDown.tsx";
import { FormattedMessage } from "react-intl";
import { useFormik } from "formik";
import { PreferencesSchema } from "sr/utils/schemas/cleaner/PreferencesSchema";
import { createPreferences } from "sr/utils/api/cleaner/createPreferences";
import { useNavigate } from "react-router-dom";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { CleanerComponentProps } from "./CleanerComponentProps";
import { getPreferences } from "sr/utils/api/cleaner/getPreferences";
import { updatePreferences } from "sr/utils/api/cleaner/updatePreferences";
import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import { ButtonAuth } from "sr/helpers/ui-components/ButtonAuth";
import { employment } from "../company/jobpost/component/Jsonalldata";
import { LoaderFullScreen } from "sr/layout/common/LoaderFullScreen";
import { renderRequiredLabel } from "sr/utils/helper";
import { deletePreference } from "sr/utils/api/cleaner/deletePreference";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";

const CleanerPreferences: ForwardRefRenderFunction<
  any,
  CleanerComponentProps
> = (
  {
    HandleStepper = null,
    page = "",
    type = "",
    dataAddedHandle = null,
    id = "",
  },
  ref
) => {
  const intl = useIntl();
  const radiusLabel = renderRequiredLabel(
    intl.formatMessage({
      id: "INPUT.RADIUS.TITLE",
      defaultMessage: "Radius (in Miles)",
    })
  );
  const shiftTypeLabel = renderRequiredLabel(
    intl.formatMessage({
      id: "CLEANER.PROFILE.PREFERENCES.SHIFTTYPE",
      defaultMessage: "Select Shift Type",
    })
  );
  const minHoursLabel = renderRequiredLabel(
    intl.formatMessage({
      id: "CLEANER.PROFILE.PREFERENCES.MINWORKHOURS",
      defaultMessage: "Min hours Work(per week)",
    })
  );
  const maxHoursLabel = renderRequiredLabel(
    intl.formatMessage({
      id: "CLEANER.PROFILE.PREFERENCES.MAXWORKHOURS",
      defaultMessage: "Max hours Work(per week)",
    })
  );
  const navigate = useNavigate();
  const [AddMoreClicked, setAddMoreClicked] = useState(false);
  const [userData, setUserData] = useState(null);
  const [preference, setPreference] = useState(null);
  const [loader, setLoader] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [data, setData] = useState(null);
  const [activeButton, setActiveButton] = useState<null | "save" | "add">(null);
  const [editingPreferenceId, setEditingPreferenceId] = useState("");
  useEffect(() => {
    const getdetails = getUserDetails();
    setUserData(getdetails);
  }, []);
  const initialValues = {
    cleaner_id: userData?.user_id,
    radius: "",
    shift_type: "",
    min_hours: "",
    max_hours: "",
  };
  const initialValuesPut = {
    cleaner_id: userData?.user_id,
    radius: preference?.radius,
    shift_type: preference?.shift_type,
    min_hours: preference?.min_hours,
    max_hours: preference?.max_hours,
    id: id,
  };
  useEffect(() => {
    const fetchData = async () => {
      setLoader(true);
      try {
        const preference = await getPreferences(undefined, id);

        if (preference.success === true) {
          setPreference(preference?.data);
          initialValues[id] = id;
          setLoader(false);
        }
      } catch (e: any) {}
    };
    if (page === "popup" && type === "put") {
      fetchData();
    }
  }, [id]);

  const {
    errors,
    handleChange,
    handleSubmit,
    values,
    submitForm,
    touched,
    handleBlur,
    setFieldTouched,
    setValues,
    resetForm,
  } = useFormik({
    initialValues:
      page === "popup" && type === "put" ? initialValuesPut : initialValues,
    enableReinitialize: true,
    validationSchema: PreferencesSchema,
    onSubmit: async (values, helpers) => {
      setButtonLoader(true);
      let result;
      if (page === "popup" && type === "put") {
        result = await updatePreferences(values);
      } else if (editingPreferenceId) {
        result = await updatePreferences({
          ...values,
          id: editingPreferenceId,
        });
      } else {
        result = await createPreferences(values);
      }
      if (result.success === true) {
        setButtonLoader(false);
        setActiveButton(null);
        if (page === "popup") {
          dataAddedHandle(true);
        }
        if (AddMoreClicked) {
          helpers.resetForm();
          fetchPreferenceData(userData?.user_id);
          setAddMoreClicked(false);
        } else if (editingPreferenceId) {
          resetPreferenceForm();
          fetchPreferenceData(userData?.user_id);
        } else {
          if (HandleStepper !== null) {
            HandleStepper(5);
          }
        }
      } else {
        setButtonLoader(false);
        setActiveButton(null);
      }
    },
  });
  const handleChangeWithTouch = (e: any) => {
    handleChange(e);
    if (e?.target?.name) {
      setFieldTouched(e.target.name, true, false);
    }
  };
  const handleSaveClick = () => {
    setAddMoreClicked(false);
    setActiveButton("save");
  };
  const handleAddMoreClick = () => {
    setAddMoreClicked(true);
    setActiveButton("add");
  };
  const resetPreferenceForm = () => {
    setEditingPreferenceId("");
    resetForm();
    setActiveButton(null);
  };
  React.useImperativeHandle(ref, () => ({
    submitForm,
  }));
  const fetchPreferenceData = async (cleanerId) => {
    try {
      setActiveButton(null);
      setButtonLoader(true);
      const preferenceData = await getPreferences(cleanerId, undefined);
      if (preferenceData?.success) {
        setData(preferenceData.data);
        setButtonLoader(false);
        setActiveButton(null);
      } else {
        setButtonLoader(false);
        setActiveButton(null);
      }
    } catch (e: any) {
      setButtonLoader(false);
      setActiveButton(null);
    }
  };
  useEffect(() => {
    if (userData?.user_id) {
      if (page !== "popup" && type !== "put") {
        fetchPreferenceData(userData?.user_id);
      }
    }
  }, [userData?.user_id]);

  const handleEditPreference = (item) => {
    setEditingPreferenceId(item?.id);
    setValues({
      cleaner_id: userData?.user_id,
      radius: item?.radius || "",
      shift_type: item?.shift_type || "",
      min_hours: item?.min_hours || "",
      max_hours: item?.max_hours || "",
    });
    setAddMoreClicked(false);
    setActiveButton(null);
  };

  const handleDeletePreference = async (item) => {
    if (!item?.id) return;

    setButtonLoader(true);
    const queryParams = new URLSearchParams();
    queryParams.append("id", item.id);
    const result = await deletePreference(queryParams);
    setButtonLoader(false);

    if (result?.success === true) {
      if (editingPreferenceId === item.id) {
        resetPreferenceForm();
      }
      fetchPreferenceData(userData?.user_id);
    }
  };
  return (
    <ContentWithSpinner loader={loader}>
      {buttonLoader && <LoaderFullScreen />}
      <form className="mt-5" onSubmit={handleSubmit} noValidate>
        <Input
          value={values.radius}
          error={touched.radius && errors.radius}
          onChange={handleChangeWithTouch}
          handleBlur={handleBlur}
          label={radiusLabel}
          type="number"
          name="radius"
          min={1}
          max={100}
        ></Input>
        <DropDown
          error={touched.shift_type && errors.shift_type}
          value={values.shift_type}
          onChange={handleChangeWithTouch}
          onBlur={handleBlur}
          name="shift_type"
          label={shiftTypeLabel}
        >
          <option selected>
            {" "}
            <FormattedMessage
              id="CLEANER.PROFILE.PREFERENCES.CHOOSE.SHIFTTYPE"
              defaultMessage="Choose Shift Type"
            />{" "}
          </option>
          {employment.map((data, index) => (
            <option key={index} value={data.value}>
              {" "}
              <FormattedMessage id={data.label}></FormattedMessage>{" "}
            </option>
          ))}
        </DropDown>
        <Input
          value={values.min_hours}
          error={touched.min_hours && errors.min_hours}
          onChange={handleChangeWithTouch}
          handleBlur={handleBlur}
          name="min_hours"
          label={minHoursLabel}
          type="number"
          placeholder={intl.formatMessage({
            id: "CLEANER.PROFILE.PREFERENCES.MINWORKHOURS.PLACEHOLDER",
            defaultMessage: "Enter minimum hours work",
          })}
        ></Input>
        <Input
          value={values.max_hours}
          error={touched.max_hours && errors.max_hours}
          onChange={handleChangeWithTouch}
          handleBlur={handleBlur}
          name="max_hours"
          label={maxHoursLabel}
          type="number"
          placeholder={intl.formatMessage({
            id: "CLEANER.PROFILE.PREFERENCES.MAXWORKHOURS.PLACEHOLDER",
            defaultMessage: "Enter maximum hours work",
          })}
        ></Input>
        {(page === "popup" && type === "add") || type === "put" ? (
          ""
        ) : (
          <>
            <div className="mt-5 flex space-x-5">
              <ButtonAuth
                loader={buttonLoader && activeButton === "save"}
                disabled={buttonLoader}
                onClick={handleSaveClick}
              >
                {editingPreferenceId ? (
                  "Update Preferences"
                ) : (
                  <FormattedMessage
                    id="REGISTER.BUTTON.SAVEANDCONTINUE"
                    defaultMessage="Save and Continue"
                  />
                )}
              </ButtonAuth>
              {!editingPreferenceId && (
                <ButtonAuth
                  loader={buttonLoader && activeButton === "add"}
                  disabled={buttonLoader}
                  onClick={handleAddMoreClick}
                >
                  Add More
                </ButtonAuth>
              )}
              {editingPreferenceId && (
                <button
                  type="button"
                  className="mt-5 mb-5 flex h-10 w-32 items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 text-sm font-bold text-gray-700 hover:bg-gray-100"
                  onClick={resetPreferenceForm}
                >
                  <FontAwesomeIcon icon={faXmark} />
                  Cancel
                </button>
              )}
              {!editingPreferenceId && (
                <div
                  className="rounded-full text-center
                 bg-[#ff39b3] font-bold
                 p-2
                 text-white
                 w-44
                 cursor-pointer mb-5 mt-5
                 hover:bg-navy"
                  onClick={() => HandleStepper(5)}
                >
                  Skip{" "}
                </div>
              )}
            </div>
            {data?.length > 0 && (
              <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="grid grid-cols-[1fr_1.2fr_1fr_1fr_96px] bg-gray-50 px-3 py-2 text-[12px] font-bold uppercase text-gray-600">
                  <div>Radius</div>
                  <div>Employment Type</div>
                  <div>Min Hours</div>
                  <div>Max Hours</div>
                  <div className="text-right">Actions</div>
                </div>
                {data?.map((item) => (
                  <div
                    key={item?.id}
                    className={`grid grid-cols-[1fr_1.2fr_1fr_1fr_96px] items-center border-t border-gray-100 px-3 py-3 text-[14px] font-medium text-gray-800 ${
                      editingPreferenceId === item?.id ? "bg-blue-50" : ""
                    }`}
                  >
                    <div>{item?.radius || "-"}</div>
                    <div>{item?.shift_type || "-"}</div>
                    <div>{item?.min_hours || "-"}</div>
                    <div>{item?.max_hours || "-"}</div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-[#0078ae] hover:bg-gray-50"
                        onClick={() => handleEditPreference(item)}
                        title="Edit preferences"
                      >
                        <FontAwesomeIcon icon={faPencil} />
                      </button>
                      <button
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => handleDeletePreference(item)}
                        title="Delete preferences"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </form>
    </ContentWithSpinner>
  );
};
export default forwardRef(CleanerPreferences);
